import { graphql } from 'react-relay';

import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { containsNetworkError } from '@confluence/network';
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import type {
	fallbackMediaNameFetcherQuery$data,
	fallbackMediaNameFetcherQuery$variables,
} from './__generated__/fallbackMediaNameFetcherQuery.graphql.ts';

const PAGE_SIZE = 50;
// setting max iterations to avoid ever getting into an infinite loop just in case
const MAX_ITERATIONS = 50;

const fallbackMediaNameFetcherQueryNode = graphql`
	query fallbackMediaNameFetcherQuery($contentId: ID!, $offset: Int, $first: Int) {
		content(id: $contentId, status: ["current", "draft"]) {
			edges {
				node {
					attachments(first: $first, offset: $offset) {
						nodes {
							title
							extensions {
								key
								value
							}
						}
						pageInfo {
							hasNextPage
						}
					}
				}
			}
		}
	}
`;

/**
 * Creates a fallbackMediaNameFetcher for MediaInlineCard that resolves the attachment
 * filename from Confluence GraphQL using the media file UUID.
 *
 * Paginates through all attachments on the page (PAGE_SIZE at a time) until the
 * matching fileId is found, short-circuiting as soon as a match is found.
 * Uses cache-first so Apollo deduplicates concurrent in-flight requests and caches
 * already-fetched pages within the same session.
 *
 * Used as a workaround for #hot-301450 where DC -> Cloud migrated media files
 * are missing filenames in the media service.
 *
 * Gated behind the `platform_editor_media_name_fallback` experiment at the call site.
 */
export const createFallbackMediaNameFetcher =
	(contentId: string): ((id: string) => Promise<string>) =>
	async (id: string): Promise<string> => {
		let offset = 0;
		let iterations = 0;
		let hasNextPage = true;

		while (hasNextPage && iterations < MAX_ITERATIONS) {
			const { data } = await queryInterop<
				fallbackMediaNameFetcherQuery$data,
				fallbackMediaNameFetcherQuery$variables
			>({
				query: fallbackMediaNameFetcherQueryNode,
				variables: { contentId, offset, first: PAGE_SIZE },
				fetchPolicy: 'cache-first',
			}).catch((e) => {
				if (isUnauthorizedError(e) || containsNetworkError(e)) {
					markErrorAsHandled(e);
				}
				return { data: null };
			});

			const attachments = data?.content?.edges?.[0]?.node?.attachments;
			const nodes = attachments?.nodes ?? [];

			for (const node of nodes) {
				const fileId = node?.extensions?.find((ext) => ext?.key === 'fileId')?.value;
				if (fileId === id && node?.title) {
					return node.title;
				}
			}

			hasNextPage = attachments?.pageInfo?.hasNextPage ?? false;
			offset += PAGE_SIZE;
			iterations += 1;
		}

		throw new Error(`Attachment with fileId ${id} not found on page ${contentId}`);
	};
